/**
 * Display formatting for prompt-defined label/value data.
 *
 * This is the TypeScript counterpart of `backend/text.py` and follows
 * the same rules on purpose: what the user reads here should match what the
 * backend embedded into the search index, otherwise a value can be visible on
 * screen yet phrased differently in the text that search actually matches on.
 */
import type { FieldValue, Fields } from "./api";

/** Whether a value has content worth showing. A real 0 is kept; blanks are not. */
export function isPresent(value: FieldValue): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  return true;
}

/** Render a raw key from the model as a display label. */
export function prettyKey(key: string): string {
  const spaced = key.replace(/_/g, " ").trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Flatten one value to text, expanding lists and nested objects. */
export function formatValue(value: FieldValue): string {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return value.map(formatValue).join(", ");
  if (typeof value === "object") {
    return Object.entries(value)
      .map(([key, inner]) => `${prettyKey(key)}: ${formatValue(inner)}`)
      .join(", ");
  }
  return String(value);
}

/**
 * One extracted value, ready to render.
 *
 * `lines` rather than a string, because most of what a drawing yields is a list -
 * the notes, the dimensions, the rows of a parts list - and joining those with
 * commas produced the single worst thing to read in the app: a nine-point welding
 * specification as one wrapped paragraph, its own "1.", "2." and "3." buried
 * mid-sentence. Each entry is its own line here, and the caller lays them out.
 */
export interface ValueEntry {
  label: string;
  lines: string[];
}

/** Turn a possibly nested object into ordered, display-ready entries. */
export function flatten(values: Fields | null | undefined): ValueEntry[] {
  if (!values) return [];
  return Object.entries(values)
    .filter(([, value]) => isPresent(value))
    .map(([key, value]) => ({ label: prettyKey(key), lines: valueLines(value) }));
}

/**
 * Split one value into the lines it displays as.
 *
 * A list gives one line per entry. A single value gives one line - unless the
 * sheet itself broke it, as a title block does with a two-line title, in which
 * case its own line breaks are kept rather than flattened into a run-on.
 */
export function valueLines(value: FieldValue): string[] {
  if (Array.isArray(value)) {
    return value.filter(isPresent).map((item) => formatValue(item).trim());
  }
  return formatValue(value)
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

/** Render an ISO timestamp for the grid, falling back to the raw string. */
export function formatDate(iso: string): string {
  if (!iso) return "-";
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}` +
    ` ${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`
  );
}

/** Shorten a long filename for chips and breadcrumbs. */
export function shortName(name: string, max: number): string {
  return name.length > max ? `${name.slice(0, max - 1).trimEnd()}...` : name;
}

/** How close to the top of the sheet a region has to be to have no room above it. */
const FLUSH_WITH_TOP = 0.03;

/**
 * The classes for one region outline drawn over a page image.
 *
 * Both screens that draw regions need the same three things said about a box: it
 * is a region, whether it is the selected one, and whether it sits too close to
 * the top of the sheet for its tag to go in the margin above it.
 */
export function regionClass(
  bbox: [number, number, number, number] | null | undefined,
  selected: boolean,
): string {
  const flush = !bbox || bbox[1] < FLUSH_WITH_TOP;
  return ["region", selected ? "region--on" : "", flush ? "region--flush" : ""]
    .filter(Boolean)
    .join(" ");
}

/** Wrap stored base64 JPEG data as a data URL the browser can render. */
export function imageSrc(b64: string): string {
  return `data:image/jpeg;base64,${b64}`;
}

/** One row of a table read back out of a list value. */
export interface ValueRow {
  cells: string[];
  /**
   * True when the sheet wrote this row with fewer cells than the table has -
   * which on a parts list means a total line, and is worth setting apart from
   * the items it totals.
   */
  total: boolean;
}

/** A list value that turned out to be the rows of a table. */
export interface ValueTable {
  /** The header row, or null for a table whose first row is already data. */
  head: string[] | null;
  rows: ValueRow[];
  columns: number;
}

const CELL = " | ";

/**
 * The cell separator as written, and as it has to be read back.
 *
 * Written as `" | "`, but a row with an empty cell writes two separators back to
 * back - "TOTAL ASSEMBLY MASS |  |  | 703.8" - and splitting that on the literal
 * string leaves stray "|" fragments standing in for the blanks, because the
 * space between two pipes gets consumed by the first match. Splitting on the
 * pipe itself and trimming is the same parse without that hole.
 */
const SEPARATOR = /\s*\|\s*/;

/**
 * Read a list of rows as a table, or return null if it is not one.
 *
 * The extraction returns a parts list, a take-off or a revision block as one
 * string per row with its cells separated by `" | "`, header row included. Shown
 * as text that is a wall: eleven rows of a bill of materials run together read
 * as one paragraph of part numbers, and the thing a reader wants from a BOM -
 * scan down the QTY column - is impossible. The cells are already there, so the
 * table is only ever a parse away.
 *
 * Conservative on purpose: a list of notes or dimensions has no separator and
 * stays a list, and anything ragged enough to be doubtful is left as one too.
 */
export function asTable(lines: string[]): ValueTable | null {
  if (lines.length < 2 || !lines.every((line) => line.includes("|"))) return null;

  const split = lines.map((line) => line.split(SEPARATOR).map((cell) => cell.trim()));
  const columns = modalLength(split);
  if (columns < 2) return null;

  const [first, ...rest] = split;
  // A header row names its columns, so it carries no readings - and a reading is
  // what digits mean here. "ITEM | DESCRIPTION | QTY" is a header; "Pipe class |
  // A1A" is the first row of a two-column data table that never had one, and
  // emphasising it as a heading would misrepresent the sheet.
  const heading = first.length === columns && !first.some((cell) => /\d/.test(cell));
  return {
    head: heading ? first : null,
    rows: (heading ? rest : split).map((row) => fold(row, columns)),
    columns,
  };
}

/** The cell count most rows have, which is what the table is that wide. */
function modalLength(rows: string[][]): number {
  const tally = new Map<number, number>();
  for (const row of rows) tally.set(row.length, (tally.get(row.length) ?? 0) + 1);
  let best = 0;
  let seen = 0;
  for (const [length, count] of tally) {
    if (count > seen || (count === seen && length > best)) {
      best = length;
      seen = count;
    }
  }
  return best;
}

/**
 * Fit one row to the table's width.
 *
 * A row with too many cells has the extras folded back into its last one, so no
 * reading is dropped to make a grid line up. A short row is a total - "DOORS
 * TOTAL | 21" under a five-column schedule - and is laid out the way the sheet
 * lays it out: the label on the left, the figure under the column it totals, and
 * the space between them empty.
 */
function fold(row: string[], columns: number): ValueRow {
  if (row.length === columns) return { cells: row, total: false };
  if (row.length > columns) {
    return {
      cells: [...row.slice(0, columns - 1), row.slice(columns - 1).join(CELL)],
      total: false,
    };
  }
  const tail = row.slice(1);
  return {
    cells: [row[0], ...Array(columns - row.length).fill(""), ...tail],
    total: true,
  };
}
